import { useGetSearchParamsObj } from '@/src/hooks/srp/searchParams'
import useSearchResults from '@/src/hooks/srp/useSearchResults'
import { AIEventInput, buildAIEventPayload } from '@/src/utils/analytics/ai/aiEvents'
import { isEmpty } from '@/src/utils/generalUtils'
import { logError } from '@/src/utils/logError'
import { useCallback, useMemo } from 'react'
import useClevertapEvent from '../index'

const useClevertapForSrp = () => {
    const { triggerEvent } = useClevertapEvent()
    const { sid, selectedSort, cards, sortData, filterData } = useSearchResults()
    const { urlQueryObj } = useGetSearchParamsObj()

    const searchQuery = useMemo(() => {
        const {
            from = '',
            to = '',
            class: flightClass,
            origin,
            destination,
            depart_date,
            return_date,
            intl,
            adults,
            childs,
            infants
        } = urlQueryObj || {}

        return {
            from,
            to,
            flightClass,
            origin,
            destination,
            depart_date,
            return_date,
            intl,
            adults,
            childs,
            infants
        }
    }, [urlQueryObj])

    const flightCounts = useMemo(() => {
        const onwardFlightsCount = cards?.[0]?.length || 0
        const returnFlightsCount = cards?.[1]?.length || 0

        return {
            onwardFlightsCount,
            returnFlightsCount
        }
    }, [cards])

    const priceCalculations = useMemo(() => {
        const onwardCardID = cards?.[0]?.[Number(sortData?.price_up?.[0]?.[0]?.split(':')[1])]
        const returnCardID = cards?.[1]?.[Number(sortData?.price_up?.[1]?.[0]?.split(':')[1])]
        const onwardMinPrice = onwardCardID?.priceBreakup?.pr || 0
        const returnMinPrice = returnCardID?.priceBreakup?.pr || 0

        return {
            onwardMinPrice,
            returnMinPrice
        }
    }, [cards])

    const nonStopFlights = useMemo(() => {
        const nonStopFlightsCountOnward = cards?.[0]?.filter(card => card.maxStopsInSectors === 0).length || 0
        const nonStopFlightsCountReturn = cards?.[1]?.filter(card => card.maxStopsInSectors === 0).length || 0

        return {
            nonStopFlightsCountOnward,
            nonStopFlightsCountReturn
        }
    }, [cards])

    const nearbyAirportCalculations = useMemo(() => {
        const allNbGroups = filterData?.nbAirportFilter?.flat() || []
        const nearbyAirportCount =
            allNbGroups.reduce((acc, curr) => acc + (curr.options.length || 0), 0) || 'N/A'

        const isDefaultNearbyAirportFilterSelected = allNbGroups.some(
            group => group.selectedValues?.length > 0
        )
            ? 'yes'
            : 'no'

        const OnwardNearbyAirportFlightsCount = cards?.[0]?.filter(card => !isEmpty(card.nbAirports)).length || 0
        const ReturnNearbyAirportFlightsCount = cards?.[1]?.filter(card => !isEmpty(card.nbAirports)).length || 0

        return {
            nearbyAirportCount,
            isDefaultNearbyAirportFilterSelected,
            OnwardNearbyAirportFlightsCount,
            ReturnNearbyAirportFlightsCount
        }
    }, [cards])

    const getDx = useCallback((departDate: string) => {
        if (!departDate) return 'N/A'
        const [day, month, year] = departDate.split('/')
        const departureDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day))
        const today = new Date()
        today.setHours(0, 0, 0, 0)
        departureDate.setHours(0, 0, 0, 0)
        return Math.floor((departureDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24))
    }, [])

    const getMaxPrice = useCallback(() => {
        const onwardCards = cards?.[0] || []
        const returnCards = cards?.[1] || []
        const allCards = [...onwardCards, ...returnCards]
        if (allCards.length === 0) return 0
        return Math.max(...allCards.map(card => card.priceBreakup?.pr || 0))
    }, [cards])

    const getMinPrice = useCallback(() => {
        const onwardCards = cards?.[0] || []
        const returnCards = cards?.[1] || []
        const allCards = [...onwardCards, ...returnCards]
        if (allCards.length === 0) return 0
        return Math.min(...allCards.map(card => card.priceBreakup?.pr || 0))
    }, [cards])

    const triggerSrpEvent = useCallback(
        (name: string, payload: any) => {
            const finalPayload = {
                search_id: sid,
                origin: searchQuery.from,
                destination: searchQuery.to,
                travel_class: searchQuery.flightClass,
                // origin_name: searchQuery.origin,
                // destination_name: searchQuery.destination,
                // origin_destination: `${searchQuery.origin}_${searchQuery.destination}`,
                trip_type: !!searchQuery.return_date ? 'RoundTrip' : 'OneWay',
                route_type: searchQuery.intl === 'yes' ? 'International' : 'Domestic',
                adult_count: Number(searchQuery.adults) || 1,
                child_count: Number(searchQuery.childs) || 0,
                infant_count: Number(searchQuery.infants) || 0,
                departure_date: searchQuery.depart_date,
                return_date: searchQuery.return_date || '',

                Onward_Count: flightCounts.onwardFlightsCount,
                Return_Count: searchQuery.return_date ? flightCounts.returnFlightsCount : 'N/A',
                result_count: flightCounts.onwardFlightsCount + flightCounts.returnFlightsCount,

                min_price: getMinPrice(),
                max_price: getMaxPrice(),

                non_stop_flights: nonStopFlights.nonStopFlightsCountOnward + nonStopFlights.nonStopFlightsCountReturn,
                direct_available_flights:
                    nonStopFlights.nonStopFlightsCountOnward + nonStopFlights.nonStopFlightsCountReturn,

                srp_entry_source: 'flights_home',
                page_name: 'flights_srp',
                last_page_name: 'flights_home',
                round_trip_view: 'solution',

                nearby_airports: nearbyAirportCalculations.nearbyAirportCount,
                nearby_airport_flights:
                    nearbyAirportCalculations.OnwardNearbyAirportFlightsCount +
                    nearbyAirportCalculations.ReturnNearbyAirportFlightsCount,
                default_nearby_airports_on: nearbyAirportCalculations.isDefaultNearbyAirportFilterSelected,

                filter_type: selectedSort?.[0]?.sortKey === 'price_up' ? 'price_sort' : selectedSort?.[0]?.sortKey,
                filter_value: selectedSort?.[0]?.sortKey === 'price_up' ? 'sort_ascending' : selectedSort?.[0]?.sortKey,

                dx: getDx(searchQuery.depart_date),
                current_page: 'flight_srp',
                ...payload
            }
            triggerEvent(name, finalPayload)
        },
        [
            triggerEvent,
            sid,
            searchQuery,
            selectedSort,
            nearbyAirportCalculations,
            flightCounts,
            nonStopFlights,
            getMinPrice,
            getMaxPrice,
            getDx
        ]
    )

    /**
     * AI-mode events on the SRP (Air_PageLoad on auto-open, AI chat query + action,
     * manual chat open). Reuses `triggerSrpEvent` so the full SRP search context
     * (search_id, route, counts, page_name='flights_srp', …) is merged in alongside
     * the normalised AI keys.
     */
    const triggerSrpAIEvent = useCallback(
        (name: string, input: AIEventInput) => {
            try {
                triggerSrpEvent(name, buildAIEventPayload(input))
            } catch (e) {
                logError('triggerSrpAIEvent', e)
            }
        },
        [triggerSrpEvent]
    )

    return {
        triggerSrpEvent,
        triggerSrpAIEvent
    }
}

export default useClevertapForSrp
